import { useRef, useState } from "react";
import { Crop, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";

export const DEFAULT_IMG = { fit: "cover", zoom: 1, pos_x: 50, pos_y: 50, height: 0 };

const imgCss = (s) => ({
  objectFit: s.fit, objectPosition: `${s.pos_x}% ${s.pos_y}%`,
  transform: s.zoom !== 1 ? `scale(${s.zoom})` : undefined, transformOrigin: `${s.pos_x}% ${s.pos_y}%`,
});

export const AdjustableImage = ({ src, st, aspect = "", className = "", imgClass = "", testid, children, alt = "" }) => {
  const s = { ...DEFAULT_IMG, ...(st || {}) };
  return (
    <div data-testid={testid} className={`relative overflow-hidden ${s.height ? "" : aspect} ${className}`}
      style={s.height ? { height: `min(${s.height}px, 80vw)` } : undefined}>
      <img src={src} alt={alt} draggable={false} className={`w-full h-full ${imgClass}`} style={imgCss(s)} />
      {children}
    </div>
  );
};

const SliderRow = ({ label, value, min, max, step, onChange, fmt, testid }) => (
  <div>
    <div className="flex justify-between text-xs text-slate-400 mb-2"><span>{label}</span><span className="font-mono">{fmt(value)}</span></div>
    <Slider data-testid={testid} value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} />
  </div>
);

const DragPreview = ({ src, s, onChange, aspect }) => {
  const box = useRef(null);
  const drag = useRef(null);
  const down = (e) => { drag.current = { x: e.clientX, y: e.clientY, px: s.pos_x, py: s.pos_y }; e.currentTarget.setPointerCapture(e.pointerId); };
  const move = (e) => {
    if (!drag.current || !box.current) return;
    const r = box.current.getBoundingClientRect();
    const k = 100 / s.zoom;
    const clamp = (v) => Math.max(0, Math.min(100, Math.round(v)));
    onChange({ pos_x: clamp(drag.current.px - ((e.clientX - drag.current.x) / r.width) * k), pos_y: clamp(drag.current.py - ((e.clientY - drag.current.y) / r.height) * k) });
  };
  return (
    <div ref={box} data-testid="image-adjust-preview" onPointerDown={down} onPointerMove={move} onPointerUp={() => { drag.current = null; }}
      className={`relative rounded-lg overflow-hidden bg-black/60 border border-[#242C3D] cursor-grab active:cursor-grabbing touch-none select-none ${s.height ? "" : aspect}`}
      style={s.height ? { height: Math.min(s.height, 360) } : undefined}>
      <img src={src} alt="" draggable={false} className="w-full h-full pointer-events-none" style={imgCss(s)} />
      <span className="absolute bottom-2 left-2 text-[11px] bg-black/60 text-white px-2 py-1 rounded">Drag to reposition</span>
    </div>
  );
};

export const ImageAdjustButton = ({ src, value, onChange, aspect = "aspect-video", testid, title = "Adjust image", allowHeight = true }) => {
  const [open, setOpen] = useState(false);
  const s = { ...DEFAULT_IMG, ...(value || {}) };
  const set = (patch) => onChange({ ...s, ...patch });
  if (!src) return null;
  const chip = (on) => `h-8 px-3 rounded-full text-xs border transition-colors ${on ? "border-indigo-500 bg-indigo-500/15 text-indigo-200" : "border-[#242C3D] text-slate-400 hover:text-white"}`;
  return (
    <>
      <button type="button" data-testid={testid} onClick={() => setOpen(true)}
        className="h-8 px-2.5 rounded-md border border-[#242C3D] hover:border-indigo-500 text-xs text-slate-300 hover:text-white flex items-center gap-1.5 transition-colors">
        <Crop className="w-3.5 h-3.5" /> Adjust
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141A24] border-[#242C3D] text-white max-w-2xl" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
          <DragPreview src={src} s={s} onChange={set} aspect={aspect} />
          <div className="grid sm:grid-cols-2 gap-5 mt-2">
            <SliderRow label="Zoom / crop" testid={`${testid}-zoom`} value={s.zoom} min={1} max={3} step={0.05} onChange={(v) => set({ zoom: v })} fmt={(v) => `${v.toFixed(2)}×`} />
            {allowHeight && <SliderRow label="Height" testid={`${testid}-height`} value={s.height} min={0} max={900} step={10} onChange={(v) => set({ height: v && v < 80 ? 80 : v })} fmt={(v) => (v ? `${v}px` : "Auto")} />}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
            <div className="flex gap-2 items-center">
              <span className="text-xs text-slate-400 mr-1">Fit</span>
              <button type="button" data-testid={`${testid}-fit-cover`} onClick={() => set({ fit: "cover" })} className={chip(s.fit === "cover")}>Fill (crop)</button>
              <button type="button" data-testid={`${testid}-fit-contain`} onClick={() => set({ fit: "contain" })} className={chip(s.fit === "contain")}>Fit whole image</button>
            </div>
            <div className="flex gap-2">
              <button type="button" data-testid={`${testid}-reset`} onClick={() => onChange({ ...DEFAULT_IMG })} className="h-9 px-3 rounded-full border border-[#242C3D] text-xs text-slate-300 hover:bg-white/5 flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" /> Reset</button>
              <button type="button" data-testid={`${testid}-done`} onClick={() => setOpen(false)} className="h-9 px-5 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-medium">Done</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
